import { useEffect, useState } from 'react';
import type { FunctionComponent, SVGProps } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Select } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  SearchIcon,
  NewEngagementIcon,
  CheckmarkIcon,
  StatusPlanningIcon,
  StatusPendingIcon,
  StatusAssignedIcon,
  EditSquareIcon,
  TrashIcon,
  TaskBloodIcon,
  TaskTeacherIcon,
  TaskMediaIcon,
  TaskRoadshowIcon,
} from '../../assets/icons';
import { useGetEngagementQuery, engName, engOwnerName } from '../../features/engagements/engagementsApi';
import {
  useGetEngagementTasksQuery,
  useDeleteTaskMutation,
  taskTitle,
  taskLocation,
  taskTags,
  taskDate,
  taskRawStatus,
  taskAssigneeName,
  statusKey,
  API_STATUS,
  type Task,
  type UiTaskStatus,
} from '../../features/tasks/tasksApi';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/** Tile presentation cycles across tasks (the API carries no icon). */
const TILES: { Tile: IconType; tileBg: string; tileColor: string }[] = [
  { Tile: TaskBloodIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#3b82f6]' },
  { Tile: TaskTeacherIcon, tileBg: 'bg-[#fff7ed]', tileColor: 'text-[#f97316]' },
  { Tile: TaskMediaIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#3b82f6]' },
  { Tile: TaskRoadshowIcon, tileBg: 'bg-[#fff7ed]', tileColor: 'text-[#f97316]' },
];

/** Compact tag chips — shows the first two, then a "+N" overflow chip. */
function TagChips({ tags }: { tags: string[] }) {
  if (tags.length === 0) return <span className="text-xs text-[#9ca3af]">—</span>;
  const shown = tags.slice(0, 2);
  const extra = tags.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <span
          key={t}
          className="inline-flex max-w-[84px] items-center truncate rounded-full bg-brand-shade px-2.5 py-1 text-xs font-medium text-brand"
        >
          {t}
        </span>
      ))}
      {extra > 0 && <span className="text-xs font-medium text-ink-secondary">+{extra}</span>}
    </div>
  );
}

const GRID = 'grid grid-cols-[minmax(0,1fr)_180px_160px_130px_150px_80px] items-center';
const TH = 'text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]';

/** Two-letter initials for a coordinator avatar (the API carries no photo). */
function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function StatusCell({ status }: { status: UiTaskStatus }) {
  if (status === 'scheduled')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#16a34a]">
        <span className="size-1.5 rounded-full bg-[#22c55e]" />
        SCHEDULED
      </span>
    );
  if (status === 'completed')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#4b5563]">
        <CheckmarkIcon className="size-4" />
        COMPLETED
      </span>
    );
  if (status === 'inprogress')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#2563eb]">
        <span className="h-1.5 w-3 rounded-sm bg-[#3b82f6]" />
        IN PROGRESS
      </span>
    );
  if (status === 'assigned')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#4b5563]">
        <StatusAssignedIcon className="size-4" />
        ASSIGNED
      </span>
    );
  if (status === 'pending')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#6b7280]">
        <StatusPendingIcon className="size-4" />
        PENDING
      </span>
    );
  return (
    <span className="flex items-center gap-1.5 text-xs font-bold text-[#ea580c]">
      <StatusPlanningIcon className="size-4" />
      PLANNING
    </span>
  );
}

export default function EngagementDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: engagement } = useGetEngagementQuery(id!, { skip: !id });
  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

  const name = engagement ? engName(engagement) : 'Engagement';
  const owner = engagement ? engOwnerName(engagement) : '';
  const overview = engagement?.description ?? '';

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  // The status filter holds a UI key; the server expects the title-case label.
  const statusParam = status ? API_STATUS[status as UiTaskStatus] : undefined;

  const { data, isFetching } = useGetEngagementTasksQuery(
    {
      engagementId: id!,
      ...(search ? { search } : {}),
      ...(statusParam ? { status: statusParam } : {}),
      page,
      pageSize,
    },
    { skip: !id },
  );

  const pageRows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;

  const goTask = (t: Task) =>
    navigate(`/user/engagements/${id ?? ''}/tasks/${t.id}`, {
      state: { name, taskName: taskTitle(t) },
    });

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    await deleteTask(taskToDelete.id).unwrap();
    setTaskToDelete(null);
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs items={[{ label: 'Engagements', to: '/user/engagements' }, { label: name }]} />

      {/* Title row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[#bbf7d0] bg-[#dcfce7] px-3 py-1 text-sm font-medium text-[#15803d]">
            <span className="size-1.5 rounded-full bg-[#22c55e]" />
            {(engagement?.status ?? 'ACTIVE').toUpperCase()}
          </span>
          <h1 className="text-2xl font-semibold leading-8 text-ink">{name}</h1>
        </div>
        {owner && (
          <div className="rounded border border-line/20 bg-white px-4 py-3">
            <p className="text-base font-medium text-ink">{owner}</p>
            <p className="text-sm text-ink-secondary">Engagement Owner</p>
          </div>
        )}
      </div>

      {/* Overview */}
      {overview && (
        <div className="flex flex-col gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.6px] text-[#64748b]">Overview</span>
          <div className="rounded-lg border border-line-soft bg-white px-5 py-5 shadow-card">
            <p className="text-sm leading-[22px] text-ink-secondary">{overview}</p>
          </div>
        </div>
      )}

      {/* Tasks card */}
      <div className="flex flex-col gap-5 rounded-xl border border-[#f3f4f6] bg-white p-4 shadow-card">
        {/* Toolbar */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-[384px]">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search tasks.."
              className="w-full rounded-lg border border-[#e5e7eb] bg-white py-2.5 pl-10 pr-3 text-sm text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              className="w-[150px]"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={[
                { value: '', label: 'Status: All' },
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'completed', label: 'Completed' },
                { value: 'inprogress', label: 'In Progress' },
                { value: 'pending', label: 'Pending' },
                { value: 'assigned', label: 'Assigned' },
                { value: 'planning', label: 'Planning' },
              ]}
            />
            <button
              type="button"
              onClick={() => navigate(`/user/engagements/${id ?? ''}/tasks/new`, { state: { name } })}
              className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              <NewEngagementIcon className="size-4" />
              New Task
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-[#e5e7eb]">
          <div className="overflow-x-auto">
            <div className="min-w-[840px]">
              {/* Header */}
              <div className={[GRID, 'border-b border-[#e5e7eb] px-6 py-4'].join(' ')}>
                <span className={TH}>Task Details</span>
                <span className={TH}>Tags</span>
                <span className={TH}>Status</span>
                <span className={TH}>Date</span>
                <span className={TH}>Coordinator</span>
                <span className={[TH, 'text-right'].join(' ')}>Actions</span>
              </div>

              {/* Rows */}
              {isFetching && total === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-ink-secondary">Loading tasks…</div>
              ) : pageRows.length === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-ink-secondary">No tasks found.</div>
              ) : (
                pageRows.map((t, i) => {
                  const tile = TILES[(start + i) % TILES.length];
                  const coord = taskAssigneeName(t);
                  return (
                    <div
                      key={t.id}
                      className={[GRID, 'px-6 py-4', i > 0 ? 'border-t border-[#f3f4f6]' : ''].join(' ')}
                    >
                      {/* Task details */}
                      <div className="flex items-center gap-4 pr-4">
                        <span className={['flex size-10 shrink-0 items-center justify-center rounded-full', tile.tileBg, tile.tileColor].join(' ')}>
                          <tile.Tile className="size-5" />
                        </span>
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() => goTask(t)}
                            className="truncate text-left text-sm font-semibold text-brand hover:underline"
                          >
                            {taskTitle(t)}
                          </button>
                          <span className="truncate text-xs text-[#6b7280]">{taskLocation(t)}</span>
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="pr-3">
                        <TagChips tags={taskTags(t)} />
                      </div>

                      {/* Status */}
                      <div>
                        <StatusCell status={statusKey(taskRawStatus(t))} />
                      </div>

                      {/* Date */}
                      <span className="text-sm text-[#4b5563]">{taskDate(t) || '—'}</span>

                      {/* Coordinator */}
                      <div className="flex items-center gap-2.5">
                        {coord ? (
                          <>
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-shade text-xs font-semibold text-brand">
                              {initials(coord)}
                            </span>
                            <span className="truncate text-sm font-medium text-[#374151]">{coord}</span>
                          </>
                        ) : (
                          <span className="text-sm italic text-[#9ca3af]">Unassigned</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          aria-label="Edit task"
                          onClick={() =>
                            navigate(`/user/engagements/${id ?? ''}/tasks/${t.id}/edit`, {
                              state: { name, taskName: taskTitle(t) },
                            })
                          }
                          className="text-ink-secondary transition-colors hover:text-brand"
                        >
                          <EditSquareIcon className="size-4" />
                        </button>
                        <button
                          type="button"
                          aria-label="Delete task"
                          onClick={() => setTaskToDelete(t)}
                          className="text-ink-secondary transition-colors hover:text-[#ef4444]"
                        >
                          <TrashIcon className="size-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}

              {/* Footer */}
              <Pagination
                rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
                rangeEnd={Math.min(page * pageSize, total)}
                total={total}
                noun="activities"
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(v) => {
                  setPageSize(v);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        open={!!taskToDelete}
        title="Delete task"
        message={
          taskToDelete
            ? `Are you sure you want to delete "${taskTitle(taskToDelete)}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete task"
        loading={isDeleting}
        onClose={() => setTaskToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
